<template>
  <div class="paragraph-block" v-html="renderedHtml" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { renderMarkdown } from '../markdown';

interface ParagraphBlock {
  type: 'paragraph';
  text?: string;
  markdown?: string;
}

const props = defineProps<{ block: ParagraphBlock }>();

const renderedHtml = computed(() => {
  const content = props.block.markdown ?? props.block.text ?? '';
  return renderMarkdown(content);
});
</script>

<style scoped lang="scss">
.paragraph-block {
  color: var(--oa-text-primary);
  line-height: 1.75;
  font-size: var(--oa-text-base);
  margin: 14px 0;

  &:first-child { margin-top: 0; }
  &:last-child { margin-bottom: 0; }

  :deep(p) {
    margin: 0 0 14px 0;
    &:last-child { margin-bottom: 0; }
  }

  :deep(a) {
    color: var(--oa-gold-full);
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;

    &:hover {
      color: var(--oa-gold-bright);
    }
  }

  :deep(strong) {
    color: var(--oa-text-primary);
    font-weight: 700;
    background: linear-gradient(
      180deg,
      transparent 60%,
      color-mix(in srgb, var(--oa-gold-full) 18%, transparent) 60%
    );
    padding: 0 1px;
  }

  :deep(em) {
    font-style: italic;
    color: color-mix(in srgb, var(--oa-text-primary) 92%, var(--oa-gold-full));
  }

  :deep(code) {
    background: var(--oa-bg-3);
    padding: 2px 6px;
    border-radius: var(--oa-radius-sm);
    font-family: var(--oa-font-mono);
    font-size: 0.875em;
    color: var(--oa-gold-bright, var(--oa-gold-full));
  }

  :deep(ul), :deep(ol) {
    margin: 12px 0;
    padding-left: 24px;
  }

  :deep(li) {
    margin-bottom: 6px;
    line-height: 1.65;
  }

  :deep(li::marker) {
    color: var(--oa-gold-full);
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 18px 0;
    font-size: var(--oa-text-sm);
    background: var(--oa-bg-2);
    border-radius: var(--oa-radius-md);
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  }

  :deep(thead) {
    background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
  }

  :deep(th) {
    text-align: left;
    padding: 10px 14px;
    font-weight: 700;
    color: var(--oa-gold-full);
    border-bottom: 1px solid color-mix(in srgb, var(--oa-gold-full) 20%, transparent);
    font-size: var(--oa-text-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  :deep(td) {
    padding: 10px 14px;
    border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 30%, transparent);
    color: var(--oa-text-primary);
  }

  :deep(tr:last-child td) {
    border-bottom: none;
  }

  :deep(blockquote) {
    border-left: 3px solid var(--oa-gold-dim);
    padding: 8px 16px;
    margin: 14px 0;
    color: var(--oa-text-secondary);
    font-style: italic;
    background: color-mix(in srgb, var(--oa-bg-3) 20%, transparent);
    border-radius: 0 var(--oa-radius-md) var(--oa-radius-md) 0;
  }

  :deep(hr) {
    border: none;
    border-top: 1px solid color-mix(in srgb, var(--oa-border) 60%, transparent);
    margin: 20px 0;
  }
}
</style>
